import NiceModal, { useModal } from '@ebay/nice-modal-react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useResendInvitationMutation } from '../../hooks/useMutations/useCompaniesMutations'
import { useCompanyUsers } from '../../hooks/useQueries/useCompanies'
import { useSnackbar } from '../../hooks/useSnackbar'
import { LoaderButton } from '../LoaderButton'

export const ResendInvitationModal = NiceModal.create(
  ({ companyId, email }: { companyId: string; email: string }) => {
    const modal = useModal()
    const { t } = useTranslation()
    const { data: users } = useCompanyUsers(companyId)
    const userId = users?.find(user => user.user.email === email)?.user.id
    const mutation = useResendInvitationMutation(companyId)
    const { showSnackbar } = useSnackbar()

    const handleSave = () => {
      if (userId) {
        mutation.mutate(
          { companyId, userId },
          {
            onSuccess: () => {
              showSnackbar({
                message: t('resend_invitation_modal.success'),
                severity: 'success'
              })
            },
            onError: () => {
              showSnackbar({
                message: t('resend_invitation_modal.error'),
                severity: 'error'
              })
            },
            onSettled: () => {
              modal.remove()
            }
          }
        )
      }
    }

    return (
      <Dialog
        open={modal.visible}
        onClose={() => modal.remove()}
        sx={{
          '& .MuiDialog-paper': {
            width: '37.5rem',
            padding: '1.5rem',
            borderRadius: '.25rem'
          }
        }}
      >
        <DialogTitle sx={{ py: '2rem' }}>
          <Typography fontWeight='bold' variant='h6' component='span'>
            {t('resend_invitation_modal.title')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack direction='column' spacing='.5rem'>
            <Typography variant='body1'>
              {t('resend_invitation_modal.text_1')}
            </Typography>
            <Typography variant='body1'>
              {t('resend_invitation_modal.text_2')}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            color='primary'
            onClick={() => modal.remove()}
            sx={{ textTransform: 'none' }}
          >
            {t('common.cancel')}
          </Button>
          <LoaderButton
            onClick={handleSave}
            disabled={mutation.isPending}
            variant='contained'
            loading={mutation.isPending}
            label={t('resend_invitation_modal.button')}
            sx={{ textTransform: 'none' }}
          />
        </DialogActions>
      </Dialog>
    )
  }
)
